import { useEffect, useLayoutEffect, useState } from "react";
import EditorPreviewContainer from "../EditorPreviewContainer";
import { FlyerForm } from "../FlyerEditor";
import FlyerPreview from "../FlyerPreview";
import SvgWrappableText from "../preview/SvgWrappableText";
import useLayout from "../util/useLayout";
import SvgWidthResizeText from "../preview/SvgWidthResizeText";
import pxFromCm from "../util/pxFromCm";
import SvgText from "../preview/SvgText";
import RadioGroup from "../components/RadioGroup";
import { useTranslation } from "react-i18next";
import { useFlyerConfig } from "../util/useFlyerConfig";
import { Spacer } from "../editor/EditorHelpers";
import BackgroundPicker, { BackgroundProps } from "../components/BackgroundPicker";
import { useRestorable, v } from "../util/useRestorable";
import { PresetTextEditor } from "../components/TextEditor";
import { useIsHardNo } from "../util/useDomain";
import { FlyerType } from "../FlyerTypePicker";
import Checkbox from "../components/Checkbox";
import { countryFromLocale } from "../locales/locales";

type StarEffect = 'star-burst' | 'none';

export default function EuroSticker() {
  const { t } = useTranslation();
  const { flyerLocale } = useFlyerConfig();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const { isSmallScreen } = useLayout();
  const [starEffect, setStarEffect] = useState<StarEffect>('none');
  const [wantsCircleWithFlag, setWantsCircleWithFlag] = useState<boolean>(flyerLocale !== 'en_EU');
  const [bigHeader, setBigHeader] = useRestorable(dt('default.buy-european.title').toUpperCase(), 'bigHeader');
  const [subheader, setSubheader] = useRestorable(dt('default.boycott-usa.title').toUpperCase(), 'subheader');
  const [background, setBackground] = useState<BackgroundProps>({ backgroundColor: "#002266" });
  const [flagExists, setFlagExists] = useState(false);
  const country = countryFromLocale(flyerLocale);

  useEffect(() => {
    const path = `/flag_${country.toLowerCase()}_cc.png`;

    const checkImage = async () => {
      try {
        const response = await fetch(path, { method: 'HEAD' });
        const contentType = response.headers.get('Content-Type') || '';

        if (response.ok && contentType.includes('image/png')) {
          setFlagExists(true);
        } else {
          setFlagExists(false);
        }
      } catch (error) {
        setFlagExists(false);
      }
    };

    checkImage();
  }, [country]);

  return (
    <EditorPreviewContainer>
      <FlyerForm $isSmallScreen={isSmallScreen}>
        <PresetTextEditor 
          label={t('big-header.label')}
          value={bigHeader}
          onChange={setBigHeader}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('subheader.label')}
          value={subheader}
          onChange={setSubheader}
        />
        {flagExists && <Spacer $height="15px"/>}
        {flagExists && <Checkbox
          id={'include-flag'}
          checked={wantsCircleWithFlag || false}
          onChange={() => setWantsCircleWithFlag(!wantsCircleWithFlag)}
          label={t('replace-a-star-with-country-flag.label')}
        />}
        <Spacer $height="15px"/>
        <RadioGroup 
          label={t('star-effect.label')}
          options={[
            {
              id: 'radio.star-burst',
              value: 'star-burst',
              label: t('star-burst.option')
            },
            {
              id: 'radio.none',
              value: 'none',
              label: t('none.option')
            },
          ]}
          selectedValue={starEffect}
          onChange={(v) => setStarEffect(v as StarEffect)}
        />
        <Spacer $height="15px" />
        <BackgroundPicker 
          label={t('background.label')}
          defaultColor={background.backgroundColor}
          defaultImage={null}
          onChange={setBackground} 
        />
      </FlyerForm>
      <FlyerPreview 
        background={background} 
        getAnalyticsDetails={() => JSON.stringify({
          bigHeader,
          subheader,
          starEffect
        })} >
        <EuroStickerPreview 
          starEffect={starEffect}
          bigHeader={v(bigHeader)}
          subheader={v(subheader)}
          flagLocale={(wantsCircleWithFlag && flagExists) ? flyerLocale : null}
        />
      </FlyerPreview>
    </EditorPreviewContainer>
  )
}

interface EuroStickerPreviewProps {
  starEffect: StarEffect;
  bigHeader: string;
  subheader: string;
  flagLocale: string | null;
}

const EuroStickerPreview: React.FC<EuroStickerPreviewProps> = ({
  starEffect, bigHeader, subheader, flagLocale,
}) => {
  const [mainTextHeight, setMainTextHeight] = useState(0);
  const [subtextHeight, setSubtextHeight] = useState(0);
  const [adjustY, setAdjustY] = useState(0);
  const [bboxes, setBBoxes] = useState([]);
  const width = 496 * 5;
  const height = 496 * 5;
  const gap = 60;
  const subtextWidth = 700;
  const mainTextTop = adjustY + 0.5 * (height - mainTextHeight - subtextHeight - (subtextHeight == 0 ? 0 : gap));
  const subtextTop = mainTextTop + mainTextHeight + gap;

  const handleMainTextLayout = (h, boxes) => {
    setMainTextHeight(h);
    setBBoxes(boxes);
  }

  const handleSubtextLayout = (ch, d) => {
    setSubtextHeight(ch + d);
  }

  useEffect(() => {
    console.log("flagLocale: ", flagLocale);
  }, [flagLocale]);

  const euroStars = [
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
    [ [ "#FFD213" ] ],
  ]

  const { flyerType } = useFlyerConfig();
  const isEuroSticker = flyerType === FlyerType.EURO_STICKER;


  const reachLimit = isEuroSticker ? 800 : 680;
  const euroRingRadius = 0.5 * height - 230;
  const margin = pxFromCm(0.75);
  const starBurst = starEffect === 'star-burst';

  const [recursion, setRecursion] = useState(0);
  const [maxTextReach, setMaxTextReach] = useState(0);
  const [fontSize, setFontSize] = useState(260);

  useLayoutEffect(() => {
    if (!subheader) {
      setSubtextHeight(0);
    }
    setRecursion(0);
    setFontSize((prev) => {
      if (prev === 260) {
        return 255;
      }
      return 260;
    });
  }, [subheader, bigHeader])

  useLayoutEffect(() => {
    if (!bboxes || bboxes.length === 0) {
      return;
    }
    const yOffset = mainTextTop - bboxes[0].y;
    const max = bboxes.reduce((acc, bbox) => {
      const getR = (x, y) => {
        const dx = x - (0.5 * width);
        const dy = (y + yOffset) - (0.5 * height);
        return Math.sqrt(dx * dx + dy * dy);
      }
      return Math.max(
        acc,
        [
          getR(bbox.x, bbox.y),
          getR(bbox.x, bbox.y + bbox.height),
          getR(bbox.x + bbox.width, bbox.y),
          getR(bbox.x + bbox.width, bbox.y + bbox.height),
        ].reduce((acc, v) => Math.max(acc, v), 0)
      );
    }, 0)
    setMaxTextReach(max);
    const w0 = bboxes[0].width;
    const w1 = bboxes[bboxes.length - 1].width;
    if (w1 > 0) {
      const ratio = w0 / w1;
      const maxAdjust = 60;
      if (ratio > 2) {
        setAdjustY(maxAdjust);
      } else if (ratio < 0.5) {
        setAdjustY(-maxAdjust);
      } else if (ratio < 1) {
        const f = (1 / ratio) - 1;
        setAdjustY(-f * maxAdjust);
      } else {
        const f = ratio - 1;
        setAdjustY(f * maxAdjust);
      }
    }
  }, [bboxes]);

  useEffect(() => {
    if (maxTextReach < 1 || recursion >= 20) {
      return;
    }
    if (maxTextReach > reachLimit) {
      setTimeout(() => {
        setFontSize(fontSize - 10);
        setRecursion(recursion + 1);  
      }, 5);
    }
  }, [maxTextReach])

  const { isHardNo } = useIsHardNo();
  const hideOneStar = !!flagLocale;
  const country = flagLocale ? countryFromLocale(flagLocale).toLowerCase() : undefined;
  const flagImagePath = flagLocale ? `/flag_${country}_cc.png` : undefined;
  const outlineFlag = flagImagePath 
    ? (country === 'uk'
      || country === 'fi'
      || country === 'fr'
      || country === 'be'
    )
    : false; 

  return (
    <svg width="100%" height="100%" className="has-custom-font">
      <defs>
        {flagImagePath && <pattern id="imageFill" patternUnits="objectBoundingBox" width="1" height="1">
          <image 
            href={flagImagePath} 
            x="0" 
            y="0" 
            width="320" 
            height="320" 
            preserveAspectRatio="xMidYMid slice" 
          />
        </pattern>}
      </defs>      
      <SvgWrappableText
        text={bigHeader}
        color={"#FFFFFF"}
        x={width / 2}
        top={mainTextTop}
        fontFamily='Hardno, Helvetica, Arial, sans-serif'
        fontSize={fontSize}
        fontWeight="400"
        letterSpacing={fontSize * 0.10}
        width={reachLimit * 1.4}
        textAnchor="middle"
        onLayout={handleMainTextLayout}
      />
      {false && <circle
        cx={0.5 * width}
        cy={0.5 * height}
        r={reachLimit} 
        fill="none"
        stroke="#5500ee"
        strokeWidth={12}
      />}
      {false && bboxes.map((bbox) => {
        if (!bbox) {
          return (<rect 
            x={400}
            y={400}
            width={800}
            height={800}
            fill="none"
            stroke="black"
            strokeWidth={8}
          />)
        }
        const yOffset = mainTextTop - bboxes[0].y;
        return (
          <rect 
            x={bbox.x}
            y={bbox.y + yOffset}
            width={bbox.width}
            height={bbox.height}
            fill="none"
            stroke="#ee00dd"
            strokeWidth={12}
          />
        );
      })}
      {subheader && <SvgWidthResizeText 
        text={subheader}
        color={"#FFFFFF"}
        x={0.5 * (width - subtextWidth)}
        capTop={subtextTop}
        width={subtextWidth}
        maxFontSize={120}
        onLayout={handleSubtextLayout}
        hasEffect={false}
      />}
      {<SvgText 
        text={isHardNo ? "hardno.eu" : "boycott-flyer.eu"}
        color={"rgba(255, 255, 255, 0.5"}
        x={width - margin}
        y={height - margin}
        textAnchor="end"
        fontSize={40}
        fontWeight="600"
        hasEffect={false}
      />}
      {isEuroSticker && euroStars.map((sc, index) => {
        const a = index * (360 / 12);
        const arad = a * Math.PI / 180;
        const r = euroRingRadius;         
        return (index === 10 && hideOneStar)
          ? (
            <g key={index}>
            <circle 
              r={outlineFlag ? 162.5 : 159}
              cx={0.5 * width + r * Math.sin(arad)}
              cy={0.5 * height - r * Math.cos(arad)}
              fill="white"
            />
            <circle 
              r={160}
              cx={0.5 * width + r * Math.sin(arad)}
              cy={0.5 * height - r * Math.cos(arad)}
              fill="url(#imageFill)"
            />
            </g>)
          : (<SvgLayeredStar 
          key={index}
          cx={0.5 * width + r * Math.sin(arad)}
          cy={0.5 * height - r * Math.cos(arad)}
          r={155}
          sc={sc}
          starBurst={starBurst}
        />)
      })}
    </svg>
  )
}

interface SvgLayeredStarProps {
  cx: number;
  cy: number;
  r: number;
  sc: (string | number)[][];
  outline?: boolean;
  starBurst?: boolean;
  startAngle?: number;
  drawCircles?: boolean;
}

const SvgLayeredStar: React.FC<SvgLayeredStarProps> = ({
  cx, cy, r, sc, outline, starBurst, startAngle = 0, drawCircles = false
}) => {
  const hasOutline = outline || false;
  const hasStarBurst = starBurst || false;
  const calcRadius = (i, ws, r) => {
    if (i == 0) {
      return r;
    }
    const sumWeights = ws.reduce((s, v) => s + v, 0);
    const usedWeights = ws.slice(0, i).reduce((s, v) => s + v, 0);
    const proportional = (1 - usedWeights / sumWeights) * r;
    const squareBased = ws.slice(0, i).reduce((rAndUsed, w) => {      
      const r = rAndUsed[0]
      const usedWeights = rAndUsed[1];
      const remWeights = sumWeights - usedWeights;
      const newR = Math.sqrt((remWeights - w) * r * r / remWeights);
      const newUsed = usedWeights + w;
      return [newR, newUsed];
    }, [r, 0])[0];
    return 0.5 * (proportional + squareBased);
  }

  return (
    <g>
      {hasOutline && !drawCircles && <SvgStar
        cx={cx}
        cy={cy}
        r={r * 1.1}
        fill={'rgba(255, 255, 255, 0.5'}
      />}
      {hasOutline && drawCircles && <circle
        cx={cx}
        cy={cy}
        r={r * 0.75 * 1.1}
        fill={'rgba(255, 255, 255, 0.5'}
      />}
      {hasStarBurst && <SvgStar
        cx={cx}
        cy={cy}
        r={r * 1.25}
        armCount={20}
        ratio={0.3}
        startAngle={startAngle + 360 / 40}
        fill={'rgba(255, 255, 255, 0.5'}
      />}
      {sc.map((c, i) => {
        const ir = calcRadius(i, sc.map((c) => c[1]), r)
        if (drawCircles) {
          return (<circle
            cx={cx}
            cy={cy}
            r={ir * 0.75}
            fill={c[0].toString()}
          />)
        }
        return (
          <SvgStar
            key={i}
            cx={cx}
            cy={cy}
            r={ir}
            startAngle={startAngle}
            fill={c[0].toString()}
          />
        )
      })}
    </g>

  )
}

interface SvgStarProps {
  cx: number;
  cy: number;
  r: number;
  fill: string;
  armCount?: number;
  ratio?: number;
  startAngle?: number;
}

const SvgStar: React.FC<SvgStarProps> = ({
  cx, cy, r, fill, armCount, ratio, startAngle
}) => {
  const coords: string[] = [];
  armCount = armCount || 5;
  ratio = ratio || 0.382;
  startAngle = startAngle || 0;
  for (let a = 0; a < 360; a += 360 / armCount) {
    const arad0 = (startAngle + a) * Math.PI / 180;
    const arad1 = (startAngle + a + 360 / armCount / 2) * Math.PI / 180;
    const x0 = cx + r * Math.sin(arad0);
    const y0 = cy - r * Math.cos(arad0);
    coords.push(`${x0.toFixed(1)} ${y0.toFixed(1)}`)
    const x1 = cx + ratio * r * Math.sin(arad1);
    const y1 = cy - ratio * r * Math.cos(arad1);
    coords.push(`${x1.toFixed(1)} ${y1.toFixed(1)}`)
  }
  const d = "M" + coords.join(" L") + "Z";
  return (<path 
    fill={fill}
    d={d}
  />)
};